import dayjs, { Dayjs } from "dayjs";
import { onboardingLiveTimezone } from "../onboardingLiveTime.ts";

export const STATISTICS_PERIOD_WEEK_OPTIONS = [4, 12, 26] as const;
export type StatisticsPeriodWeeks =
  (typeof STATISTICS_PERIOD_WEEK_OPTIONS)[number];
export const OVERALL_STATISTICS_PERIOD = "overall";
export type StatisticsPeriodChoice =
  | StatisticsPeriodWeeks
  | typeof OVERALL_STATISTICS_PERIOD;
export const STATISTICS_PERIOD_CHOICES: StatisticsPeriodChoice[] = [
  ...STATISTICS_PERIOD_WEEK_OPTIONS,
  OVERALL_STATISTICS_PERIOD,
];
export const DEFAULT_STATISTICS_PERIOD: StatisticsPeriodChoice = 4;
export const OVERALL_STATISTICS_FROM = dayjs(0);

export type StatisticsPeriod = {
  from: Dayjs;
  to: Dayjs;
  weekStartDays: string[];
  days: number;
};

const dayFormat = "YYYY-MM-DD";

function dayInRomeOf(dateTime: Dayjs | string): string {
  return dayjs(dateTime).tz(onboardingLiveTimezone).format(dayFormat);
}

function mondayOf(day: string): Dayjs {
  const calendarDay = dayjs.utc(day);
  const daysSinceMonday = (calendarDay.day() + 6) % 7;
  return calendarDay.subtract(daysSinceMonday, "day");
}

function periodStartingOn(firstDay: string, now: Dayjs): StatisticsPeriod {
  const today = dayInRomeOf(now);
  const firstMonday = mondayOf(firstDay);
  const weeks = mondayOf(today).diff(firstMonday, "week") + 1;
  return {
    from: dayjs.tz(firstDay, onboardingLiveTimezone),
    to: now,
    weekStartDays: Array.from({ length: weeks }, (_, weekIndex) =>
      firstMonday.add(weekIndex, "week").format(dayFormat),
    ),
    days: dayjs.utc(today).diff(dayjs.utc(firstDay), "day") + 1,
  };
}

export function statisticsPeriodOf(
  weeks: StatisticsPeriodWeeks,
  now: Dayjs,
): StatisticsPeriod {
  const firstMonday = mondayOf(dayInRomeOf(now)).subtract(weeks - 1, "week");
  return periodStartingOn(firstMonday.format(dayFormat), now);
}

export function overallStatisticsPeriodOf(
  firstLiveStartUtc: string | undefined,
  now: Dayjs,
): StatisticsPeriod {
  return periodStartingOn(dayInRomeOf(firstLiveStartUtc ?? now), now);
}

export function weekStartDayOf(dateTime: string): string {
  return mondayOf(dayInRomeOf(dateTime)).format(dayFormat);
}
